import * as Sentry from '@sentry/react';
import { Howl, Howler } from 'howler';

/*

    Playing audio is very difficult on certain devices. We use howler.js
    which handles cross-browser quirks (especially iOS Safari) by using
    Web Audio under the hood with HTML5 Audio fallback, and by creating
    a fresh source node per play (avoiding the "every other sound" bug).

    Design notes:

    - Success/failure is determined by the asynchronous `play`/`playerror`
      events — NOT by the synchronous return value of howl.play(), which
      returns a sound id even when the play is merely queued.

    - If an attempt times out, we howl.stop(id) the pending sound. Howler
      internally queues a play behind a suspended context ("playLock") and
      fires it when the context resumes — without the stop(), that queued
      sound plus our retry would BOTH fire (double alert).

    - Concurrent play() calls are coalesced into the in-flight attempt.
      Two messages arriving ms apart should not produce overlapping beeps,
      and it keeps the recovery ladder from interleaving with itself.

    - reset() rebuilds the Howl (fresh buffer) but NOT the AudioContext —
      Howler shares one global ctx. So if the context is suspended and a
      resume() doesn't take (no user gesture), we fail fast instead of
      burning seconds on a reset that cannot help.

    - Routine activity (successful plays, unlocks, warmups) is recorded as
      Sentry breadcrumbs so it rides along on the next real error event.
      Only anomalies are captured as standalone events.

*/

type Engine = 'html' | 'webaudio' | 'none';

let howl: Howl | null = null;
let unlocked = false;
let lastEngineUsed: Engine = 'none';
let warmedUp = false;
let inFlight: Promise<boolean> | null = null;

const stats = {
    playAttempts: 0,
    playSuccesses: 0,
    playFailures: 0,
    playCoalesced: 0,
    resets: 0,
    resumes: 0,
    warmups: 0,
};

// How long to wait for the `play` event before treating an attempt as
// failed. A loaded buffer (especially an inlined one) starts within ms;
// the headroom is for context-resume latency.
const PLAY_TIMEOUT_MS = 1500;
// How long to wait for the buffer to finish loading before/while playing.
// Irrelevant once the sound is inlined as base64, generous for networks.
const LOAD_TIMEOUT_MS = 5000;

/*
    Sound source.

    Option A (served file): subject to network load failures.
        const SOUND_SRC = process.env.PUBLIC_URL + '/sounds/alert-01.wav';

    Option B (inlined, no network — recommended for a tiny clip): paste a
    data URI. Generate it with:  base64 -i alert-01.wav
        const SOUND_SRC = 'data:audio/wav;base64,UklGRiQ...';

    NOTE: Howler cannot infer the codec from a data URI (no file
    extension), so `format` MUST be set explicitly below. If you re-encode
    to mp3/m4a to shrink the bundle, update SOUND_FORMAT to match.
*/
const SOUND_SRC = process.env.PUBLIC_URL + '/sounds/alert-01.wav';
const SOUND_FORMAT = ['wav'];

// --- diagnostics ----------------------------------------------------------

const envContext = () => ({
    visibility: document.visibilityState,
    hasFocus: document.hasFocus(),
    userActivation: (navigator as any).userActivation
        ? {
              hasBeenActive: (navigator as any).userActivation.hasBeenActive,
              isActive: (navigator as any).userActivation.isActive,
          }
        : 'unsupported',
    userAgent: navigator.userAgent,
    standalone:
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        (navigator as any).standalone === true,
});

const howlerState = () => ({
    ctxState: Howler.ctx?.state,
    usingWebAudio: Howler.usingWebAudio,
    autoUnlock: Howler.autoUnlock,
    masterVolume: Howler.volume(),
    howlState: howl?.state(),
});

const fullContext = () => ({
    ...envContext(),
    howler: howlerState(),
    unlocked,
    warmedUp,
    stats: { ...stats },
});

const breadcrumb = (message: string, data: Record<string, unknown> = {}) => {
    Sentry.addBreadcrumb({
        category: 'audio-alert',
        level: 'info',
        message,
        data: { ...data, ...fullContext() },
    });
};

// Reserved for anomalies only — each call creates a standalone Sentry event.
const event = (
    op: string,
    message: string,
    level: Sentry.SeverityLevel = 'warning',
    extra: Record<string, unknown> = {},
) => {
    Sentry.captureMessage(message, {
        level,
        tags: { feature: 'audio-alert', op },
        extra: { ...extra, ...fullContext() },
    });
};

// --- internals ------------------------------------------------------------

const createHowl = () => {
    if (howl) return;
    try {
        howl = new Howl({
            src: [SOUND_SRC],
            format: SOUND_FORMAT, // required for data URIs (no extension to sniff)
            preload: true,
            html5: false, // Web Audio path — more reliable for short clips
            onloaderror: (_id, err) => {
                event('howl-loaderror', 'Howler load error', 'error', { error: String(err) });
            },
            // We intentionally do NOT auto-replay from onplayerror — retry
            // and recovery are owned by play() so the two can't race and
            // double-fire the sound.
            onplayerror: (_id, err) => {
                breadcrumb('howl playerror (handled by play())', { error: String(err) });
            },
        });
        breadcrumb('howl created');
    } catch (err) {
        Sentry.captureException(err, {
            tags: { feature: 'audio-alert', op: 'init' },
            extra: envContext(),
        });
    }
};

/** Wait until the buffer is loaded (kicking off a load if needed). */
const waitForLoad = (timeoutMs = LOAD_TIMEOUT_MS): Promise<boolean> =>
    new Promise((resolve) => {
        const h = howl; // capture — module ref may be swapped by a reset
        if (!h) return resolve(false);
        if (h.state() === 'loaded') return resolve(true);

        let settled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            h.off('load', onLoad);
            h.off('loaderror', onErr);
            resolve(ok);
        };
        const onLoad = () => finish(true);
        const onErr = () => finish(false);

        h.once('load', onLoad);
        h.once('loaderror', onErr);
        if (h.state() === 'unloaded') h.load();
        timer = setTimeout(() => finish(false), timeoutMs);
    });

/**
 * One playback attempt, settled by the REAL `play`/`playerror` events.
 * On timeout the pending sound id is stopped, so a Howler-internal queued
 * play (waiting on context resume) can't fire later on top of our retry.
 */
const playOnce = (timeoutMs = PLAY_TIMEOUT_MS): Promise<boolean> =>
    new Promise((resolve) => {
        const h = howl; // capture — module ref may be swapped by a reset
        if (!h) return resolve(false);

        let settled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let id: number;

        const finish = (ok: boolean, timedOut = false) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            h.off('play', onPlay, id);
            h.off('playerror', onErr, id);
            if (timedOut) {
                try {
                    h.stop(id); // cancel the queued/pending sound — see header
                } catch {}
            }
            resolve(ok);
        };
        const onPlay = () => finish(true);
        const onErr = () => finish(false);

        // play() returns the sound id synchronously; the play/playerror
        // events fire asynchronously, so attaching just after is safe.
        id = h.play();
        h.once('play', onPlay, id);
        h.once('playerror', onErr, id);
        timer = setTimeout(() => finish(false, true), timeoutMs);
    });

const noteSuccess = (after = '') => {
    stats.playSuccesses++;
    lastEngineUsed = Howler.usingWebAudio ? 'webaudio' : 'html';
    unlocked = true;
    breadcrumb(`playback succeeded${after ? ` (${after})` : ''}`, { engine: lastEngineUsed });
};

const noteFailure = (op: string, message: string) => {
    stats.playFailures++;
    lastEngineUsed = 'none';
    event(op, message, 'error');
};

/** Rebuild the Howl. Internal — bypasses the in-flight guard. */
const resetEngine = async (): Promise<void> => {
    stats.resets++;
    breadcrumb('audio engine reset');
    try {
        howl?.unload();
    } catch {}
    howl = null;
    unlocked = false;
    warmedUp = false;
    createHowl();
    await Alert.unlock();
};

/** The actual play pipeline — escalating, cheap to expensive. */
const doPlay = async (): Promise<boolean> => {
    stats.playAttempts++;

    if (!howl) {
        event('play-uninit', 'Alert.play called before init', 'warning');
        stats.playFailures++;
        return false;
    }

    // 0. Ensure the buffer is loaded. On slow networks the file may still
    // be downloading — resetting in that state would restart the download,
    // so wait (bounded) instead. One reset+reload if the load truly failed.
    if (!(await waitForLoad())) {
        breadcrumb('buffer not loaded — resetting for a fresh load');
        await resetEngine();
        if (!(await waitForLoad())) {
            noteFailure('play-failure-load', 'Alert.play failed — sound never loaded');
            return false;
        }
    }

    // 1. Plain attempt.
    if (await playOnce()) {
        noteSuccess();
        return true;
    }

    // 2. Cheap recovery — a suspended context is the most common cause.
    breadcrumb('play failed — refreshing unlock and retrying');
    await Alert.unlock();
    const ctx = Howler.ctx;
    if (ctx && ctx.state !== 'running') {
        // resume() didn't take: there's no user gesture to spend. A reset
        // shares the same AudioContext, so it cannot help — fail fast.
        noteFailure(
            'play-failure-suspended',
            'Alert.play failed — context suspended, needs user gesture',
        );
        return false;
    }
    if (await playOnce()) {
        noteSuccess('after unlock');
        return true;
    }

    // 3. Expensive recovery: rebuild the Howl (fresh buffer), retry once.
    breadcrumb('play still failing — full reset and retry');
    await resetEngine();
    if (!(await waitForLoad())) {
        noteFailure('play-failure-load', 'Alert.play failed — reload after reset never loaded');
        return false;
    }
    if (await playOnce()) {
        noteSuccess('after reset');
        return true;
    }

    noteFailure('play-failure', 'Alert.play failed (after unlock + reset)');
    return false;
};

// --- public API -----------------------------------------------------------

export const Alert = {
    init() {
        const fresh = !howl;
        createHowl();
        if (fresh) breadcrumb('audio engine initialized');
    },

    /** Try to unlock audio. Returns true if context is running. */
    async unlock(): Promise<boolean> {
        try {
            const ctx = Howler.ctx;
            if (ctx && ctx.state === 'suspended') {
                await ctx.resume();
            }
            const running = !ctx || ctx.state === 'running';
            if (running && !unlocked) breadcrumb('audio unlocked');
            unlocked = running;
        } catch (err) {
            const wasUnlocked = unlocked;
            unlocked = false;
            if (wasUnlocked) event('unlock-lost', 'Audio unlock lost', 'warning', { error: String(err) });
            else breadcrumb('unlock failed', { error: String(err) });
        }
        return unlocked;
    },

    /** Silent capability check — reads state only, never plays. */
    async probe(): Promise<boolean> {
        const ctx = Howler.ctx;
        const ok = !!howl && howl.state() === 'loaded' && (!ctx || ctx.state === 'running');
        if (!ok) breadcrumb('probe failed');
        unlocked = ok;
        return ok;
    },

    /** Play an audible sound during a user gesture to warm up the pipeline. */
    async warmup(): Promise<boolean> {
        stats.warmups++;
        breadcrumb('warmup requested');
        const ok = await Alert.play();
        if (ok) {
            warmedUp = true;
            breadcrumb('warmup playback succeeded');
        } else {
            event('warmup-failure', 'Warmup playback failed', 'warning');
        }
        return ok;
    },

    /** Full reset of the audio engine. Use on PWA resume if state is degraded. */
    async reset(): Promise<void> {
        if (inFlight) {
            breadcrumb('reset skipped — play in flight');
            return;
        }
        await resetEngine();
    },

    /** Called when tab/PWA becomes visible again. */
    async handleResume(): Promise<void> {
        stats.resumes++;
        const ctx = Howler.ctx;
        const ctxBad = ctx ? ctx.state !== 'running' : false;
        const howlBad = !howl || howl.state() !== 'loaded';
        if (ctxBad || howlBad) {
            event('resume-degraded', 'Resume found degraded audio state — resetting', 'warning', {
                ctxBad,
                howlBad,
            });
            await Alert.reset();
        } else {
            breadcrumb('resume — refreshing unlock only');
            await Alert.unlock();
        }
    },

    /**
     * Play the alert sound. Resolves true only when playback actually
     * started (the `play` event fired). Concurrent calls coalesce into
     * the in-flight attempt — near-simultaneous messages produce one
     * beep, not overlapping ones.
     */
    async play(): Promise<boolean> {
        if (inFlight) {
            stats.playCoalesced++;
            breadcrumb('play coalesced into in-flight attempt');
            return inFlight;
        }
        inFlight = doPlay();
        try {
            return await inFlight;
        } finally {
            inFlight = null;
        }
    },

    isUnlocked() {
        return unlocked;
    },

    lastEngine(): Engine {
        return lastEngineUsed;
    },
};